import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import { GetAuthoritiesCERT } from '../../../redux/actions/certification/authoritiesActions';
import {
  GetWorkflowTemplateGroupsCERT,
  AllotWorkflowTemplateGroupToAuthoritiesCERT,
  UpdateWorkflowTemplateGroupAuthorityAllotmentCERT,
} from '../../../redux/actions/certification/workflowTemplateGroupsActions';

import global from '../../../global.scss';


const authorityLabel = (a) => {
  if (!a) return 'NA';
  const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
  return a.authority_name || name || a.user_name || a.primary_email || a.id;
};


const TemplateGroupAllotmentsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.certification);

  const [loadingAuthorities, setLoadingAuthorities] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const [authoritiesList, setAuthoritiesList] = useState([]);
  const [authoritiesTotal, setAuthoritiesTotal] = useState(0);

  const [groupsList, setGroupsList] = useState([]);

  const [selectedAuthority, setSelectedAuthority] = useState(null);
  const [rowBusy, setRowBusy] = useState({});

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: 'Authority Name', sort: 'authority_name' },
    { label: 'Name', sort: null },
    { label: 'Email', sort: 'primary_email' },
    { label: 'Type', sort: 'type' },
    { label: 'Allotted', sort: null },
    { label: 'Time stamps', sort: 'createdAt' },
  ];

  const handleSort = (value) => {
    if (sort === value) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setSortDirection('asc');
    }
  };

  const handleGetAuthorities = () => {
    let query = '';
    if (search) query += `&search=${search}`;
    if (sort) query += `&field=${sort}`;
    if (sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetAuthoritiesCERT(page + 1, rowsPerPage, query, setLoadingAuthorities));
  };

  const handleGetGroups = () => {
    dispatch(GetWorkflowTemplateGroupsCERT(1, 200, '', setLoadingGroups));
  };

  useEffect(() => {
    handleGetAuthorities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    handleGetGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      setAuthoritiesList(state.authoritiesList?.records || []);
      setAuthoritiesTotal(state.authoritiesList?.total || 0);
    } catch (err) {}
  }, [state.authoritiesList]);

  useEffect(() => {
    setGroupsList(state.workflowTemplateGroupsList?.records || []);
  }, [state.workflowTemplateGroupsList]);

  // Count of ACTIVE allotments per authority — used for the chip on the left
  // table so admins can see at a glance who has how many groups enabled.
  const activeAllotmentCountByAuthority = useMemo(() => {
    const counts = {};
    groupsList.forEach((g) => {
      (g.authority_allotments || []).forEach((a) => {
        if (!a.is_active) return;
        counts[a.ref_authorities_user_id] = (counts[a.ref_authorities_user_id] || 0) + 1;
      });
    });
    return counts;
  }, [groupsList]);

  // Derive the right-panel rows { group, allotment } for the selected
  // authority directly from groupsList, so an optimistic mutation to a
  // group's authority_allotments propagates immediately.
  const selectedRows = useMemo(() => {
    if (!selectedAuthority) return [];
    return groupsList.map((g) => ({
      group: g,
      allotment: (g.authority_allotments || []).find(
        (a) => a.ref_authorities_user_id === selectedAuthority.id
      ) || null,
    }));
  }, [groupsList, selectedAuthority]);

  const setBusy = (groupId, val) =>
    setRowBusy((prev) => ({ ...prev, [groupId]: val }));

  // Optimistically patch an allotment inside a group's authority_allotments
  // list — either mutating the existing row's is_active or inserting a new
  // placeholder row. Keeps the UI in sync without a full re-fetch.
  const patchAllotmentLocally = (groupId, authorityUserId, nextIsActive, createIfMissing) => {
    setGroupsList((list) => list.map((g) => {
      if (g.id !== groupId) return g;
      const existing = (g.authority_allotments || []).find(
        (a) => a.ref_authorities_user_id === authorityUserId
      );
      if (existing) {
        return {
          ...g,
          authority_allotments: g.authority_allotments.map((a) => (
            a.ref_authorities_user_id === authorityUserId
              ? { ...a, is_active: nextIsActive }
              : a
          )),
        };
      }
      if (!createIfMissing) return g;
      return {
        ...g,
        authority_allotments: [
          ...(g.authority_allotments || []),
          {
            id: `local-${Date.now()}`,
            ref_authorities_user_id: authorityUserId,
            is_active: nextIsActive,
          },
        ],
      };
    }));
  };

  // Toggle: if allotment doesn't exist, create it (active). If it exists,
  // update its is_active flag — never soft-delete via this toggle, so the
  // row and its history stay intact.
  const toggleAllotment = async (row) => {
    if (!selectedAuthority) return;
    const groupId = row.group.id;
    const authorityUserId = selectedAuthority.id;
    const nextIsActive = !(row.allotment?.is_active);
    setBusy(groupId, true);
    // Optimistic UI
    patchAllotmentLocally(groupId, authorityUserId, nextIsActive, /*createIfMissing*/ true);

    let statusCode;
    if (row.allotment) {
      ({ statusCode } = await dispatch(UpdateWorkflowTemplateGroupAuthorityAllotmentCERT(
        groupId,
        authorityUserId,
        { is_active: nextIsActive },
        () => {}
      )));
    } else {
      ({ statusCode } = await dispatch(AllotWorkflowTemplateGroupToAuthoritiesCERT(
        groupId,
        { authority_user_ids: [authorityUserId] },
        () => {}
      )));
    }
    setBusy(groupId, false);

    if (statusCode !== 200) {
      // Roll back on failure
      patchAllotmentLocally(groupId, authorityUserId, !nextIsActive, false);
    } else {
      // Re-fetch groups once so counters + allotment ids sync with server
      dispatch(GetWorkflowTemplateGroupsCERT(1, 200, '', () => {}));
    }
  };

  const groupsCount = groupsList.length;

  return (
    <Box>

      <TopBar
        title={"Template Group Allotments"}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
      />

      <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

        {/* LEFT — authority users list */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {
            loadingAuthorities
            ?
            <Box height={"300px"} className="flexCenterCenterRow">
              <CircularProgress />
            </Box>
            :
              authoritiesList.length === 0
              ?
              <Box height={"300px"} className="flexCenterCenterRow">
                <Typography>No authorities found</Typography>
              </Box>
              :
              <Table className="block">
                <TableHead>
                  <TableRow>
                    {
                      tableHeadingsList.map((heading, headingIndex) => (
                        heading.sort === null
                        ?
                        <TableCell key={headingIndex}>{heading.label}</TableCell>
                        :
                        <TableCell key={headingIndex}>
                          <TableSortLabel
                            active={sort === heading.sort}
                            direction={sort === heading.sort ? sortDirection : 'asc'}
                            onClick={() => handleSort(heading.sort)}
                          >
                            {heading.label}
                          </TableSortLabel>
                        </TableCell>
                      ))
                    }
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    authoritiesList.map((row) => {
                      const count = activeAllotmentCountByAuthority[row.id] || 0;
                      const isSelected = selectedAuthority?.id === row.id;
                      return (
                        <TableRow
                          key={row.id}
                          onClick={() => setSelectedAuthority(row)}
                          className="table__clickable-row"
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'rgba(44, 45, 47, 1)' : 'transparent',
                          }}
                        >
                          <TableCell>{row.authority_name || <NA />}</TableCell>
                          <TableCell>
                            {(row.first_name || row.last_name)
                              ? `${row.first_name || ''} ${row.last_name || ''}`.trim()
                              : <NA />}
                          </TableCell>
                          <TableCell>{row.primary_email || <NA />}</TableCell>
                          <TableCell>{row.type || <NA />}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant={count > 0 ? 'filled' : 'outlined'}
                              color={count > 0 ? 'primary' : 'warning'}
                              label={`${count} / ${groupsCount}`}
                            />
                          </TableCell>
                          <TableCell>
                            <TableTimeStamps
                              createdAt={row.created_at || row.createdAt}
                              updatedAt={row.updated_at || row.updatedAt}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  }
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      sx={{ borderBottom: 0 }}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      colSpan={tableHeadingsList.length}
                      count={authoritiesTotal}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
          }
        </Box>

        {/* RIGHT — per-authority group checklist (dark card, matches .block) */}
        <Box
          sx={{
            width: '400px',
            flexShrink: 0,
            position: 'sticky',
            top: '20px',
            borderRadius: '16px',
            background: 'rgba(44, 45, 47, 0.64)',
            border: '1px solid rgba(44, 45, 47, 0.72)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(18.3px)',
            WebkitBackdropFilter: 'blur(18.3px)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: '16px 20px' }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: global['primary_dark'] || '#8ecce7' }}
            >
              {selectedAuthority ? authorityLabel(selectedAuthority) : 'Select an authority'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: global['primary_light'] || '#e2f2f9', opacity: 0.7 }}
            >
              {selectedAuthority
                ? 'Toggle certification programs on/off for this authority user. Turning off keeps the allotment record — it just deactivates.'
                : 'Click an authority in the table to manage its template group allotments.'}
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

          {
            !selectedAuthority
            ?
            <Box sx={{ p: '40px 20px', textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{ color: global['primary_light'] || '#e2f2f9', opacity: 0.5 }}
              >
                No authority selected.
              </Typography>
            </Box>
            :
              loadingGroups
              ?
              <Box sx={{ p: '30px 20px', textAlign: 'center' }}>
                <CircularProgress size={22} />
              </Box>
              :
                selectedRows.length === 0
                ?
                <Box sx={{ p: '30px 20px', textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: global['primary_light'] || '#e2f2f9', opacity: 0.5 }}
                  >
                    No template groups available.
                  </Typography>
                </Box>
                :
                <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                  {
                    selectedRows.map((row, idx) => {
                      const g = row.group;
                      const isChecked = !!(row.allotment && row.allotment.is_active);
                      const isInactiveAllotment = !!row.allotment && !row.allotment.is_active;
                      const globallyOff = !g.is_globally_enabled || !g.is_globally_visible;

                      return (
                        <Box key={g.id}>
                          {idx > 0 && (
                            <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
                          )}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '12px',
                              p: '12px 20px',
                              transition: 'background 120ms ease',
                              '&:hover': { background: 'rgba(255,255,255,0.03)' },
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: '2px' }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  noWrap
                                  sx={{ color: global['primary_light'] || '#e2f2f9' }}
                                >
                                  {g.name}
                                </Typography>
                                {
                                  isInactiveAllotment
                                  &&
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label="Paused"
                                  />
                                }
                              </Stack>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{ color: global['primary_light'] || '#e2f2f9', opacity: 0.6, display: 'block' }}
                              >
                                {g.description}
                              </Typography>

                              {
                                globallyOff
                                &&
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: '4px' }}>
                                  {!g.is_globally_enabled && (
                                    <Chip
                                      size="small"
                                      color="error"
                                      variant="filled"
                                      icon={<BlockOutlinedIcon sx={{ fontSize: 14 }} />}
                                      label="Globally disabled"
                                      sx={{ height: 20, fontSize: 10 }}
                                    />
                                  )}
                                  {!g.is_globally_visible && (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      variant="filled"
                                      icon={<VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />}
                                      label="Globally hidden"
                                      sx={{ height: 20, fontSize: 10 }}
                                    />
                                  )}
                                </Stack>
                              }
                              {
                                isChecked && !globallyOff
                                &&
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: '4px' }}>
                                  <CheckCircleOutlineIcon sx={{ fontSize: 13, color: global['success'] || '#2e7d32' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: 10.5, color: global['success'] || '#2e7d32' }}
                                  >
                                    Live for this authority
                                  </Typography>
                                </Stack>
                              }
                            </Box>
                            <Switch
                              checked={isChecked}
                              disabled={!!rowBusy[g.id]}
                              onChange={() => toggleAllotment(row)}
                            />
                          </Box>
                        </Box>
                      );
                    })
                  }
                </Box>
          }
        </Box>

      </Box>

    </Box>
  );
};

export default TemplateGroupAllotmentsPage;
